import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import {
  ArrowLeftIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { placementsAPI, clientsAPI, facilitiesAPI } from '../../services/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { useToast } from '../../context/ToastContext';

interface PlacementFormData {
  clientId: string;
  facilityId: string;
  status: string;
  priority: string;
  startDate: string;
  endDate?: string;
  moveInDate?: string;
  monthlyRate?: number;
  paymentMethod?: string;
  depositAmount?: number;
  contractSigned: boolean;
  contractSignedDate?: string;
  depositPaid: boolean;
  depositPaidDate?: string;
  insuranceVerified: boolean;
  insuranceVerifiedDate?: string;
  medicalRecordsTransferred: boolean;
  medicalRecordsTransferredDate?: string;
  familyNotified: boolean;
  familyNotifiedDate?: string;
  notes?: string;
}

const PlacementForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success: showSuccess, error: showError } = useToast();
  const isEditing = !!id;

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<PlacementFormData>({
    defaultValues: {
      status: 'In Progress',
      priority: 'Medium',
      contractSigned: false,
      depositPaid: false,
      insuranceVerified: false,
      medicalRecordsTransferred: false,
      familyNotified: false
    }
  });

  // Watch checkbox values to conditionally show date fields
  const contractSigned = watch('contractSigned');
  const depositPaid = watch('depositPaid');
  const insuranceVerified = watch('insuranceVerified');
  const medicalRecordsTransferred = watch('medicalRecordsTransferred');
  const familyNotified = watch('familyNotified');

  // Fetch existing placement data for editing
  const {
    data: placementData,
    isLoading: isLoadingPlacement
  } = useQuery({
    queryKey: ['placement', id],
    queryFn: () => placementsAPI.getPlacement(id!),
    enabled: isEditing,
  });

  // Fetch clients for dropdown
  const {
    data: clientsData,
    isLoading: isLoadingClients
  } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsAPI.getClients(),
  });

  // Fetch facilities for dropdown
  const {
    data: facilitiesData,
    isLoading: isLoadingFacilities
  } = useQuery({
    queryKey: ['facilities'],
    queryFn: () => facilitiesAPI.getFacilities(),
  });

  const clients = clientsData?.data?.data?.clients || [];
  const facilities = facilitiesData?.data?.data?.facilities || [];

  // Populate form when editing
  useEffect(() => {
    if (isEditing && placementData?.data?.data?.placement && clients.length > 0 && facilities.length > 0) {
      const placement = placementData.data.data.placement;
      
      console.log('🔍 DEBUG: Populating form with placement data:', placement);
      console.log('🔍 DEBUG: Available clients:', clients.length);
      console.log('🔍 DEBUG: Available facilities:', facilities.length);
      
      // Handle each field specifically to ensure proper mapping
      
      // Basic Information - Wait for clients and facilities to be loaded
      if (placement.clientId) {
        const clientId = typeof placement.clientId === 'object' ? placement.clientId._id : placement.clientId;
        setValue('clientId', clientId);
        console.log('Set clientId:', clientId);
        
        // Verify client exists in the dropdown options
        const clientExists = clients.find((c: any) => c._id === clientId);
        console.log('Client exists in dropdown:', !!clientExists, clientExists?.firstName, clientExists?.lastName);
      }
      
      if (placement.facilityId) {
        const facilityId = typeof placement.facilityId === 'object' ? placement.facilityId._id : placement.facilityId;
        setValue('facilityId', facilityId);
        console.log('Set facilityId:', facilityId);
        
        // Verify facility exists in the dropdown options
        const facilityExists = facilities.find((f: any) => f._id === facilityId);
        console.log('Facility exists in dropdown:', !!facilityExists, facilityExists?.name);
      }
      
      if (placement.status) setValue('status', placement.status);
      if (placement.priority) setValue('priority', placement.priority);
      
      // Dates - format for HTML date inputs
      const dateFields = ['startDate', 'endDate', 'moveInDate', 'contractSignedDate', 'depositPaidDate', 'insuranceVerifiedDate', 'medicalRecordsTransferredDate', 'familyNotifiedDate'];
      dateFields.forEach(field => {
        if (placement[field]) {
          const dateValue = new Date(placement[field]).toISOString().split('T')[0];
          setValue(field as keyof PlacementFormData, dateValue);
          console.log(`Set ${field}:`, dateValue);
        }
      });
      
      // Financial Information
      if (placement.monthlyRate !== undefined) setValue('monthlyRate', placement.monthlyRate);
      if (placement.paymentMethod) setValue('paymentMethod', placement.paymentMethod);
      if (placement.depositAmount !== undefined) setValue('depositAmount', placement.depositAmount);
      
      // Checklist Items (boolean fields)
      const checklistFields = ['contractSigned', 'depositPaid', 'insuranceVerified', 'medicalRecordsTransferred', 'familyNotified'];
      checklistFields.forEach(field => {
        if (placement[field] !== undefined) {
          setValue(field as keyof PlacementFormData, Boolean(placement[field]));
          console.log(`Set ${field}:`, Boolean(placement[field]));
        }
      });
      
      // Notes
      if (placement.notes) setValue('notes', placement.notes);
      
      console.log('✅ Form population completed');
    }
  }, [placementData, isEditing, setValue, clients, facilities]);

  // Create/Update placement mutation
  const saveePlacementMutation = useMutation({
    mutationFn: (data: PlacementFormData) => {
      console.log('🔍 DEBUG: Raw form data received in mutation:', data);
      
      const formattedData = {
        ...data,
        // Convert empty strings to undefined for optional fields
        endDate: data.endDate || undefined,
        moveInDate: data.moveInDate || undefined,
        monthlyRate: data.monthlyRate ? Number(data.monthlyRate) : undefined,
        depositAmount: data.depositAmount ? Number(data.depositAmount) : undefined,
        contractSignedDate: data.contractSigned && data.contractSignedDate ? data.contractSignedDate : undefined,
        depositPaidDate: data.depositPaid && data.depositPaidDate ? data.depositPaidDate : undefined,
        insuranceVerifiedDate: data.insuranceVerified && data.insuranceVerifiedDate ? data.insuranceVerifiedDate : undefined,
        medicalRecordsTransferredDate: data.medicalRecordsTransferred && data.medicalRecordsTransferredDate ? data.medicalRecordsTransferredDate : undefined,
        familyNotifiedDate: data.familyNotified && data.familyNotifiedDate ? data.familyNotifiedDate : undefined,
      };

      console.log('🔍 DEBUG: Formatted data being sent to API:', formattedData);

      return isEditing 
        ? placementsAPI.updatePlacement(id!, formattedData)
        : placementsAPI.createPlacement(formattedData);
    },
    onSuccess: () => {
      showSuccess(isEditing ? 'Placement updated successfully!' : 'Placement created successfully!');
      queryClient.invalidateQueries({ queryKey: ['placements'] });
      navigate('/placements');
    },
    onError: (error: any) => {
      console.error('🔍 DEBUG: Placement creation error:', error);
      console.error('🔍 DEBUG: Error response:', error.response);
      console.error('🔍 DEBUG: Error response data:', error.response?.data);
      console.error('🔍 DEBUG: Validation errors:', error.response?.data?.errors);
      
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map((e: any) => `${e.path}: ${e.msg}`).join(', ');
        showError(`Validation errors: ${errorMessages}`);
      } else {
        showError(error.response?.data?.message || error.response?.data?.error || 'Failed to save placement');
      }
    },
  });

  const onSubmit = (data: PlacementFormData) => {
    console.log('🔍 DEBUG: Placement form submission data:', data);
    console.log('🔍 DEBUG: Data keys:', Object.keys(data));
    console.log('🔍 DEBUG: clientId:', data.clientId);
    console.log('🔍 DEBUG: facilityId:', data.facilityId);
    console.log('🔍 DEBUG: startDate:', data.startDate);
    
    saveePlacementMutation.mutate(data);
  };

  if (isLoadingPlacement || isLoadingClients || isLoadingFacilities) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link
              to="/placements"
              className="inline-flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to Placements
            </Link>
          </div>
        </div>
        <div className="mt-4">
          <h1 className="text-2xl font-bold text-gray-900 text-left">
            {isEditing ? 'Edit Placement' : 'Create New Placement'}
          </h1>
          <p className="text-gray-600 mt-2">
            {isEditing 
              ? 'Update placement information and track progress'
              : 'Create a new placement record for client and facility matching'
            }
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Client Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client *
              </label>
              <select
                {...register('clientId', { required: 'Client is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select a client...</option>
                {clients.map((client: any) => (
                  <option key={client._id} value={client._id}>
                    {client.firstName} {client.lastName}
                  </option>
                ))}
              </select>
              {errors.clientId && (
                <p className="mt-1 text-sm text-red-600">{errors.clientId.message}</p>
              )}
            </div>

            {/* Facility Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Facility *
              </label>
              <select
                {...register('facilityId', { required: 'Facility is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select a facility...</option>
                {facilities.map((facility: any) => (
                  <option key={facility._id} value={facility._id}>
                    {facility.name} - {facility.address?.city}, {facility.address?.state}
                  </option>
                ))}
              </select>
              {errors.facilityId && (
                <p className="mt-1 text-sm text-red-600">{errors.facilityId.message}</p>
              )}
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status *
              </label>
              <select
                {...register('status', { required: 'Status is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="In Progress">In Progress</option>
                <option value="Confirmed">Confirmed</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
              {errors.status && (
                <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
              )}
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority *
              </label>
              <select
                {...register('priority', { required: 'Priority is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Urgent">Urgent</option>
              </select>
              {errors.priority && (
                <p className="mt-1 text-sm text-red-600">{errors.priority.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Dates */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Important Dates</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date *
              </label>
              <input
                type="date"
                {...register('startDate', { required: 'Start date is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {errors.startDate && (
                <p className="mt-1 text-sm text-red-600">{errors.startDate.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                {...register('endDate')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Move-in Date
              </label>
              <input
                type="date"
                {...register('moveInDate')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </div>

        {/* Financial Information */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monthly Rate
              </label>
              <div className="relative">
                <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="number"
                  step="0.01"
                  {...register('monthlyRate')}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Method
              </label>
              <select
                {...register('paymentMethod')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select payment method...</option>
                <option value="Private Pay">Private Pay</option>
                <option value="Insurance">Insurance</option>
                <option value="Medicaid">Medicaid</option>
                <option value="Medicare">Medicare</option>
                <option value="VA Benefits">VA Benefits</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Deposit Amount
              </label>
              <div className="relative">
                <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="number"
                  step="0.01"
                  {...register('depositAmount')}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Placement Checklist */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Placement Checklist</h3>
          <div className="space-y-4">
            {/* Contract Signed */}
            <div className="flex items-start space-x-3">
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  {...register('contractSigned')}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
              </div>
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700">
                  Contract Signed
                </label>
                {contractSigned && (
                  <div className="mt-2">
                    <input
                      type="date"
                      {...register('contractSignedDate')}
                      className="w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Contract signed date"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Deposit Paid */}
            <div className="flex items-start space-x-3">
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  {...register('depositPaid')}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
              </div>
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700">
                  Deposit Paid
                </label>
                {depositPaid && (
                  <div className="mt-2">
                    <input
                      type="date"
                      {...register('depositPaidDate')}
                      className="w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Deposit paid date"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Insurance Verified */}
            <div className="flex items-start space-x-3">
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  {...register('insuranceVerified')}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
              </div>
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700">
                  Insurance Verified
                </label>
                {insuranceVerified && (
                  <div className="mt-2">
                    <input
                      type="date"
                      {...register('insuranceVerifiedDate')}
                      className="w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Insurance verified date"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Medical Records Transferred */}
            <div className="flex items-start space-x-3">
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  {...register('medicalRecordsTransferred')}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
              </div>
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700">
                  Medical Records Transferred
                </label>
                {medicalRecordsTransferred && (
                  <div className="mt-2">
                    <input
                      type="date"
                      {...register('medicalRecordsTransferredDate')}
                      className="w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Medical records transferred date"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Family Notified */}
            <div className="flex items-start space-x-3">
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  {...register('familyNotified')}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
              </div>
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700">
                  Family Notified
                </label>
                {familyNotified && (
                  <div className="mt-2">
                    <input
                      type="date"
                      {...register('familyNotifiedDate')}
                      className="w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Family notified date"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Notes</h3>
          <textarea
            {...register('notes')}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Add any additional notes about this placement..."
          />
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-4">
          <Link
            to="/placements"
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Saving...' : (isEditing ? 'Update Placement' : 'Create Placement')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PlacementForm;
